<script>
/**
 * Espaços — administração dos tenants da plataforma.
 *
 * Restrita a `plataforma_papel` (suporte lê, admin altera). Não confundir com
 * ser admin de um espaço: aquilo todo cliente tem; isto administra a
 * plataforma inteira, incluindo excluir dados de outra organização.
 *
 * Consome `GET/POST /tenant` (ver `backend/src/app/controllers/TenantController.js`).
 * A mesma lógica está na CLI: `npm run tenant`.
 */
import axios from 'axios';
import Layout from '@/layouts/main.vue';
import PageHeader from '@/components/page-header';
import { erro, sucesso, confirmar } from '@/helpers/common/api';

const NOVO_VAZIO = { nome: '', slug: '', subdominio: '', plano: 'padrao' };

/** "PCdoB Rio de Janeiro" → "pcdob-rio-de-janeiro" */
const paraSlug = (texto) => String(texto || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 60);

export default {
  name: 'PlataformaEspacos',
  components: { Layout, PageHeader },

  data() {
    return {
      carregando: true,
      salvando: false,
      tenants: [],
      fallbackAtivo: false,
      dnsAutomatico: false,

      modalAberto: false,
      novo: { ...NOVO_VAZIO },
      // Enquanto o usuário não digitar um slug próprio, ele acompanha o nome.
      slugManual: false,
    };
  },

  computed: {
    formValido() {
      return this.novo.nome.trim() && this.novo.slug.trim() && this.novo.subdominio.trim();
    },
  },

  watch: {
    'novo.nome': function derivarSlug(valor) {
      if (this.slugManual) return;
      this.novo.slug = paraSlug(valor);
    },
  },

  async created() {
    const user = JSON.parse(localStorage.getItem('userdata') || '{}');
    const ehPlataforma = user.plataforma_papel === 'admin' || user.plataforma_papel === 'suporte';
    if (!ehPlataforma) {
      this.$router.replace('/');
      return;
    }
    await this.carregar();
  },

  methods: {
    rotuloStatus(status) {
      return {
        ativo: 'Ativo', trial: 'Avaliação', suspenso: 'Suspenso', encerrado: 'Encerrado',
      }[status] || status;
    },

    classeStatus(status) {
      return {
        ativo: 'badge bg-success-subtle text-success',
        trial: 'badge bg-info-subtle text-info',
        suspenso: 'badge bg-warning-subtle text-warning',
        encerrado: 'badge bg-danger-subtle text-danger',
      }[status] || 'badge bg-secondary-subtle text-secondary';
    },

    async carregar() {
      this.carregando = true;
      try {
        const { data } = await axios.get('/tenant');
        this.tenants = data.tenants || [];
        this.fallbackAtivo = Boolean(data.fallbackAtivo);
        this.dnsAutomatico = Boolean(data.dnsAutomatico);
      } catch (e) {
        erro({ title: e?.response?.data?.error || 'Não foi possível carregar os espaços.' });
      } finally {
        this.carregando = false;
      }
    },

    abrirModal() {
      this.novo = { ...NOVO_VAZIO };
      this.slugManual = false;
      this.modalAberto = true;
    },

    async criar() {
      if (!this.formValido || this.salvando) return;
      this.salvando = true;
      try {
        const { data } = await axios.post('/tenant', this.novo);
        this.modalAberto = false;

        // O DNS é conveniência: o espaço já funciona pelo caminho /t/<id>/.
        // Falhar nele não é erro de criação, mas o operador precisa saber.
        const aviso = data.dns && !data.dns.ok
          ? ` O endereço no DNS não foi criado (${data.dns.motivo}) — o espaço já funciona pelo link abaixo.`
          : '';
        sucesso({ title: `Espaço "${data.tenant.nome}" criado.${aviso}` });

        await this.carregar();
      } catch (e) {
        erro({ title: e?.response?.data?.error || 'Não foi possível criar o espaço.' });
      } finally {
        this.salvando = false;
      }
    },

    encerrar(tenant) {
      confirmar({
        options: {
          title: `Encerrar "${tenant.nome}"?`,
          text: 'O acesso é bloqueado na hora. Os dados ficam preservados e dá para reativar.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Encerrar',
          cancelButtonText: 'Cancelar',
          customClass: {
            confirmButton: 'btn btn-danger w-xs me-2',
            cancelButton: 'btn btn-light w-xs',
          },
          buttonsStyling: false,
        },
        confirmado: async () => {
          try {
            const { data } = await axios.post(`/tenant/encerrar/${tenant.id}`);
            sucesso({ title: data.mensagem });
            await this.carregar();
          } catch (e) {
            erro({ title: e?.response?.data?.error || 'Falha ao encerrar.' });
          }
        },
      });
    },

    async reativar(tenant) {
      try {
        await axios.post(`/tenant/reativar/${tenant.id}`);
        sucesso({ title: `"${tenant.nome}" reativado.` });
        await this.carregar();
      } catch (e) {
        erro({ title: e?.response?.data?.error || 'Falha ao reativar.' });
      }
    },

    linkDoEspaco(tenant) {
      return `${window.location.origin}/t/${tenant.id}/`;
    },

    async copiarLink(tenant) {
      try {
        await navigator.clipboard.writeText(this.linkDoEspaco(tenant));
        sucesso({ title: 'Link copiado.' });
      } catch (_e) {
        erro({ title: 'Não foi possível copiar. Selecione o endereço e copie à mão.' });
      }
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader title="Espaços" pageTitle="Plataforma" />

    <BRow>
      <BCol lg="12">
        <BCard no-body>
          <BCardHeader class="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h5 class="mb-1">Espaços da plataforma</h5>
              <p class="text-muted mb-0 small">
                Cada espaço é uma organização com contatos, canais e configurações próprios.
              </p>
            </div>
            <BButton variant="primary" @click="abrirModal">
              <i class="ri-add-line align-bottom me-1"></i> Novo espaço
            </BButton>
          </BCardHeader>

          <BCardBody>
            <!-- Enquanto há um espaço só, qualquer endereço resolve para ele.
                 Criar o segundo desliga esse atalho — quem opera precisa saber
                 disso ANTES de clicar. -->
            <div v-if="fallbackAtivo" class="alert alert-info d-flex gap-2" role="alert">
              <i class="ri-information-line fs-5 lh-1"></i>
              <div>
                <strong>Só existe um espaço.</strong>
                Qualquer endereço leva a ele. Ao criar o segundo, cada espaço passa a ser
                acessado pelo próprio link — o de baixo, na coluna "Endereço".
              </div>
            </div>

            <div v-if="!dnsAutomatico" class="alert alert-warning d-flex gap-2" role="alert">
              <i class="ri-global-line fs-5 lh-1"></i>
              <div>
                <strong>DNS automático desligado.</strong>
                Os espaços funcionam pelo link <code>/t/&lt;id&gt;/</code> sem precisar de nada.
                Para dar um endereço próprio a um cliente, configure o Cloudflare em
                Customização → Parâmetros.
              </div>
            </div>

            <div v-if="carregando" class="text-center py-5 text-muted">
              <div class="spinner-border spinner-border-sm me-2" role="status"></div>
              Carregando…
            </div>

            <div v-else class="table-responsive">
              <table class="table align-middle table-nowrap mb-0">
                <thead class="table-light">
                  <tr>
                    <th style="width: 60px">#</th>
                    <th>Nome</th>
                    <th>Endereço</th>
                    <th>Status</th>
                    <th class="text-end">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="t in tenants" :key="t.id">
                    <td class="text-muted">{{ t.id }}</td>
                    <td>
                      <div class="fw-medium">{{ t.nome }}</div>
                      <div class="text-muted small">{{ t.slug }}</div>
                    </td>
                    <td>
                      <button
                        type="button"
                        class="btn btn-link btn-sm p-0 text-decoration-none"
                        @click="copiarLink(t)"
                        :title="linkDoEspaco(t)"
                      >
                        <i class="ri-file-copy-line align-bottom me-1"></i>/t/{{ t.id }}/
                      </button>
                      <div v-if="t.dominio_proprio" class="text-muted small">{{ t.dominio_proprio }}</div>
                    </td>
                    <td><span :class="classeStatus(t.status)">{{ rotuloStatus(t.status) }}</span></td>
                    <td class="text-end">
                      <BButton
                        v-if="t.status !== 'encerrado'"
                        size="sm" variant="soft-danger" @click="encerrar(t)"
                      >
                        Encerrar
                      </BButton>
                      <BButton v-else size="sm" variant="soft-success" @click="reativar(t)">
                        Reativar
                      </BButton>
                    </td>
                  </tr>
                  <tr v-if="!tenants.length">
                    <td colspan="5" class="text-center text-muted py-4">Nenhum espaço cadastrado.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </BCardBody>
        </BCard>
      </BCol>
    </BRow>

    <!-- Modal custom com Teleport + tokens de tema: o BModal não acompanha o
         dark mode do template (convenção do projeto). -->
    <Teleport to="body">
      <div v-if="modalAberto" class="espacos-modal-backdrop" @click.self="modalAberto = false">
        <div class="espacos-modal" role="dialog" aria-modal="true" aria-label="Novo espaço">
          <div class="espacos-modal-header">
            <h5 class="mb-0">Novo espaço</h5>
            <button type="button" class="btn-close" aria-label="Fechar" @click="modalAberto = false"></button>
          </div>

          <form class="espacos-modal-body" @submit.prevent="criar">
            <div class="mb-3">
              <label class="form-label" for="esp-nome">Nome</label>
              <input
                id="esp-nome" v-model="novo.nome" type="text" class="form-control"
                placeholder="PCdoB Rio de Janeiro" required
              />
            </div>

            <div class="mb-3">
              <label class="form-label" for="esp-slug">Identificador</label>
              <input
                id="esp-slug" v-model="novo.slug" type="text" class="form-control"
                placeholder="pcdob-rio-de-janeiro" required
                @input="slugManual = true"
              />
              <div class="form-text">
                Usado em nomes de pasta e no banco. Só letras minúsculas, números e hífen.
                Não muda depois de criado.
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label" for="esp-sub">Subdomínio</label>
              <input
                id="esp-sub" v-model="novo.subdominio" type="text" class="form-control"
                placeholder="rj" required
              />
              <div class="form-text">
                Só é usado se este cliente ganhar um endereço próprio. O espaço funciona
                sem isso, pelo link <code>/t/&lt;id&gt;/</code>.
              </div>
            </div>

            <div class="espacos-modal-footer">
              <BButton variant="light" type="button" @click="modalAberto = false">Cancelar</BButton>
              <BButton variant="primary" type="submit" :disabled="!formValido || salvando">
                {{ salvando ? 'Criando…' : 'Criar espaço' }}
              </BButton>
            </div>
          </form>
        </div>
      </div>
    </Teleport>
  </Layout>
</template>

<style scoped>
/* Tokens do tema (--vz-*) em vez de cor fixa: a mesma marcação serve light e
   dark sem duplicar regra. */
.espacos-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1055;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.5);
}

.espacos-modal {
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 0.5rem;
  background: var(--vz-body-bg);
  color: var(--vz-body-color);
  box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.3);
}

.espacos-modal-header,
.espacos-modal-footer {
  display: flex;
  align-items: center;
  padding: 1rem;
  border-color: var(--vz-border-color);
}

.espacos-modal-header {
  justify-content: space-between;
  border-bottom: 1px solid var(--vz-border-color);
}

.espacos-modal-footer {
  justify-content: flex-end;
  gap: 0.5rem;
  border-top: 1px solid var(--vz-border-color);
}

.espacos-modal-body {
  padding: 1rem;
}

/* Sem `:global(...)`: com scoped, `:global([data-bs-theme=dark]) .x` compila
   errado neste projeto. */
[data-bs-theme='dark'] .espacos-modal {
  box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.6);
}
</style>
